import { getSearchHitPageLabel } from "../utils/searchPageLabel";
import { resolveSearchHitUrl } from "../utils/searchHitUrl";
import {
  buildNewsListLookups,
  isNewsSearchHit,
  resolveNewsHitUrl,
} from "../utils/searchNewsUrl";

const apiBase = (import.meta.env.VITE_API_BASE_URL || "").replace(/\/$/, "");
const token = import.meta.env.VITE_STRAPI_TOKEN;

const CAS_SEARCH_CONTENT_TYPES = [
  "api::cas-new.cas-new",
  "api::cas-newsletter.cas-newsletter",
  "api::cas-product-list.cas-product-list",
  "api::cas-sensaphone-item.cas-sensaphone-item",
  "api::cas-raychem-item.cas-raychem-item",
  "api::cas-download.cas-download",
  "api::cas-product-category.cas-product-category",
  "api::cas-support-category.cas-support-category",
  "api::cas-sensaphone-accessories-item.cas-sensaphone-accessories-item",
  "api::cas-trace-tek-category.cas-trace-tek-category",
];

const CONTENT_TYPE_UID_LABELS = {
  "api::cas-new.cas-new": "news",
  "api::cas-newsletter.cas-newsletter": "news",
  "api::cas-product-list.cas-product-list": "product",
  "api::cas-sensaphone-item.cas-sensaphone-item": "product",
  "api::cas-raychem-item.cas-raychem-item": "product",
  "api::cas-download.cas-download": "download",
  "api::cas-product-category.cas-product-category": "product",
  "api::cas-support-category.cas-support-category": "support",
  "api::cas-sensaphone-accessories-item.cas-sensaphone-accessories-item": "product",
  "api::cas-trace-tek-category.cas-trace-tek-category": "product",
};

function searchHeaders() {
  const headers = { "Content-Type": "application/json" };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

async function parseSearchResponse(res) {
  let payload = null;
  try {
    payload = await res.json();
  } catch {
    payload = null;
  }

  if (!res.ok) {
    const message =
      payload?.error?.message ||
      payload?.message ||
      `Search failed (${res.status})`;
    throw new Error(message);
  }

  return payload;
}

function mapContentTypeLabel(contentTypeUid) {
  if (!contentTypeUid) return "content";
  if (CONTENT_TYPE_UID_LABELS[contentTypeUid]) {
    return CONTENT_TYPE_UID_LABELS[contentTypeUid];
  }
  const shortName = String(contentTypeUid).split(".").pop() || contentTypeUid;
  return shortName.replace(/^cas-/, "").replace(/-/g, " ");
}

function normalizeHit(hit, fallbackContentType) {
  const contentTypeUid = fallbackContentType || hit.contentType;
  const contentType =
    hit.contentType ||
    mapContentTypeLabel(fallbackContentType) ||
    fallbackContentType ||
    "content";
  const url = resolveSearchHitUrl(hit, contentTypeUid) || "";

  return {
    ...hit,
    title: hit.title || hit.name || "Untitled",
    contentType,
    url,
    pageLabel: getSearchHitPageLabel({
      url,
      contentType: contentTypeUid,
    }),
  };
}

async function fetchNewsListLookups(locale) {
  try {
    const query = new URLSearchParams({
      locale,
      "populate[cas_news][populate][list][populate]": "*",
    });
    const res = await fetch(`${apiBase}/cas-news-page?${query}`, {
      headers: searchHeaders(),
    });
    const payload = await parseSearchResponse(res);
    const list =
      payload?.data?.cas_news?.list ||
      payload?.data?.cas_news?.[0]?.list ||
      [];
    return buildNewsListLookups(list);
  } catch {
    return null;
  }
}

async function enrichNewsHitUrls(hits, locale) {
  const hasNewsHits = hits.some(isNewsSearchHit);
  if (!hasNewsHits) return hits;

  const lookups = await fetchNewsListLookups(locale);
  if (!lookups) return hits;

  return hits.map((hit) => {
    if (!isNewsSearchHit(hit)) return hit;

    const resolvedUrl = resolveNewsHitUrl(hit, lookups);
    if (!resolvedUrl) return hit;

    return {
      ...hit,
      url: resolvedUrl,
      pageLabel: getSearchHitPageLabel({
        url: resolvedUrl,
        contentType: hit.contentType,
      }),
    };
  });
}

/**
 * Normalize single-search flat hits or search-all grouped hits.
 */
export function normalizeSearchHits(response, fallbackContentType) {
  const data = response?.data;
  if (!Array.isArray(data)) return [];

  if (data.length > 0 && Array.isArray(data[0]?.hits)) {
    return flattenSearchAllHits(response);
  }

  return data.map((hit) => normalizeHit(hit, fallbackContentType));
}

/**
 * Search a single Strapi content type.
 */
export async function searchContent({
  q,
  contentType,
  website,
  locale,
  limit = 20,
  offset = 0,
}) {
  const query = new URLSearchParams({
    q,
    contentType,
    limit: String(limit),
    offset: String(offset),
  });

  if (website) query.set("website", website);
  if (locale) query.set("locale", locale);

  const res = await fetch(`${apiBase}/search?${query}`, {
    headers: searchHeaders(),
  });

  return parseSearchResponse(res);
}

/**
 * Search all indexed content types for a website.
 */
export async function searchAll(q, website = "cas", locale = "en", limit = 20) {
  const query = new URLSearchParams({
    q,
    limit: String(limit),
  });

  if (website) query.set("website", website);
  if (locale) query.set("locale", locale);

  const res = await fetch(`${apiBase}/search/all?${query}`, {
    headers: searchHeaders(),
  });

  return parseSearchResponse(res);
}

/**
 * Flatten grouped search-all hits into a single list.
 */
export function flattenSearchAllHits(response) {
  const groups = response?.data;
  if (!Array.isArray(groups)) return [];

  return groups.flatMap((group) => {
    const hits = group?.hits;
    if (!Array.isArray(hits)) return [];
    return hits.map((hit) =>
      normalizeHit(hit, group.contentType || hit.contentType)
    );
  });
}

function dedupeHits(hits) {
  const seen = new Set();
  return hits.filter((hit) => {
    const key = hit.id || `${hit.documentId || ""}:${hit.url || ""}`;
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * CAS website search.
 * Uses /api/search/all when it returns hits; otherwise falls back to per content-type search.
 */
export async function searchCasWebsite(q, locale = "en", limit = 20) {
  const trimmedQuery = q.trim();
  if (!trimmedQuery) return [];

  let allHits = [];

  try {
    const allResponse = await searchAll(trimmedQuery, "cas", locale, limit);
    allHits = flattenSearchAllHits(allResponse);
  } catch {
    allHits = [];
  }

  if (allHits.length > 0) {
    const enrichedHits = await enrichNewsHitUrls(
      dedupeHits(allHits).slice(0, limit),
      locale
    );
    return enrichedHits;
  }

  const perTypeLimit = Math.max(3, Math.ceil(limit / CAS_SEARCH_CONTENT_TYPES.length));
  const settled = await Promise.allSettled(
    CAS_SEARCH_CONTENT_TYPES.map(async (contentType) => {
      const response = await searchContent({
        q: trimmedQuery,
        contentType,
        website: "cas",
        locale,
        limit: perTypeLimit,
      });
      return normalizeSearchHits(response, contentType);
    })
  );

  const merged = [];
  let failureCount = 0;

  for (const result of settled) {
    if (result.status === "fulfilled") {
      merged.push(...result.value);
    } else {
      failureCount += 1;
    }
  }

  const hits = dedupeHits(merged).slice(0, limit);
  const enrichedHits = await enrichNewsHitUrls(hits, locale);

  if (enrichedHits.length === 0 && failureCount === settled.length) {
    throw new Error("Search is unavailable right now. Please try again later.");
  }

  return enrichedHits;
}
